<template>
    <v-container class="py-12">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">{{ vehicleTitle }}</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Home', to: '/home' },
                            { text: 'Custos de veículos', to: '/financeiro/veiculos', exact: true },
                            { text: vehicleTitle, disabled: true }
                        ]" />
                    </div>
                    <div class="button-group">
                        <v-btn outlined color="primary" elevation="0" :to="`/veiculos/${$route.params.id}`">Cadastro do veículo</v-btn>
                        <v-btn outlined color="primary" elevation="0" to="/financeiro/veiculos">Voltar</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-card class="mb-4">
            <v-card-text>
                <v-row dense align="center">
                    <v-col cols="12" sm="6" md="3">
                        <v-text-field dense outlined hide-details type="date" label="De" v-model="filter.start" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-text-field dense outlined hide-details type="date" label="Até" v-model="filter.end" />
                    </v-col>
                    <v-col cols="12" md="6" class="d-flex align-center flex-wrap" style="gap: 4px">
                        <v-btn color="primary" elevation="0" :loading="loading" @click="load">Filtrar</v-btn>
                        <v-btn small text color="primary" @click="setRange('month')">Este mês</v-btn>
                        <v-btn small text color="primary" @click="setRange('6m')">Últimos 6 meses</v-btn>
                        <v-btn small text color="primary" @click="setRange('12m')">Últimos 12 meses</v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-progress-linear v-if="loading && !data" indeterminate color="primary" />

        <template v-if="data">
            <div class="stat-grid mb-4">
                <stat-card label="Custo total" :value="money(summary.cost)" :hint="`fixos ${money(summary.fixed)} · combustível ${money(summary.fuel)}`" />
                <stat-card label="Manutenção" :value="money(summary.maintenance)" :hint="`${data.maintenances.length} manutenção(ões)`" />
                <stat-card label="Motorista parceiro" :value="money(summary.partner)" hint="valor por localidade das viagens" />
                <stat-card label="Receita" :value="money(summary.revenue)" color="#1450b5" :hint="`${summary.trips} viagens finalizadas`" />
                <stat-card label="Margem" :value="money(summary.margin)" :color="summary.margin < 0 ? '#d32f2f' : '#2e7d32'"
                    :hint="summary.margin_percent !== null ? percent(summary.margin_percent) + ' da receita' : '-'" />
                <stat-card label="Custo por km" :value="summary.cost_per_km !== null ? money(summary.cost_per_km) : '-'"
                    :hint="`${number(summary.km)} km · ${number(summary.liters)} L`" />
            </div>

            <!-- CUSTOS FIXOS -->
            <v-card class="mb-4">
                <v-card-title class="d-flex align-center">
                    Custos fixos
                    <v-spacer />
                    <v-btn v-if="canManage" small color="primary" elevation="0" @click="openCost()">
                        <v-icon left small>mdi-plus</v-icon>Adicionar
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <v-data-table dense :headers="costHeaders" :items="data.fixed_costs" hide-default-footer :items-per-page="-1"
                        class="app-table" no-data-text="Nenhum custo fixo cadastrado (aluguel, financiamento, IPVA, seguro...)">
                        <template v-slot:[`item.type`]="{ item }">
                            <strong>{{ item.type_name }}</strong>
                            <div class="caption grey--text">{{ [item.description, item.supplier].filter(Boolean).join(' · ') }}</div>
                        </template>
                        <template v-slot:[`item.amount`]="{ item }">
                            {{ money(item.amount) }}
                            <span class="caption grey--text">{{ frequencyText(item) }}</span>
                            <div v-if="item.frequency === 'yearly'" class="caption grey--text">≈ {{ money(item.monthly_equivalent) }}/mês</div>
                        </template>
                        <template v-slot:[`item.start_date`]="{ item }">
                            {{ date(item.start_date) }}
                            <template v-if="item.frequency !== 'once'"> até {{ item.end_date ? date(item.end_date) : 'sem fim' }}</template>
                            <div v-if="item.installments" class="caption grey--text">{{ item.installments }} parcela(s)</div>
                        </template>
                        <template v-slot:[`item.active`]="{ item }">
                            <v-chip x-small :color="item.active ? 'green' : 'grey'" dark>{{ item.active ? 'Vigente' : 'Encerrado' }}</v-chip>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                            <div v-if="canManage" class="d-flex">
                                <v-btn icon x-small @click="openCost(item)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                                <v-btn icon x-small color="error" @click="removeCost(item)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
                            </div>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>

            <!-- MÊS A MÊS -->
            <v-card class="mb-4">
                <v-card-title>Mês a mês</v-card-title>
                <v-card-text>
                    <v-data-table dense :headers="monthHeaders" :items="data.monthly" hide-default-footer :items-per-page="-1" class="app-table">
                        <template v-slot:[`item.month`]="{ item }"><strong>{{ monthName(item.month) }}</strong></template>
                        <template v-slot:[`item.fixed`]="{ item }">
                            <span :class="{ 'grey--text': !item.fixed }">{{ money(item.fixed) }}</span>
                        </template>
                        <template v-slot:[`item.fuel`]="{ item }">
                            <span :class="{ 'grey--text': !item.fuel }">{{ money(item.fuel) }}</span>
                        </template>
                        <template v-slot:[`item.maintenance`]="{ item }">
                            <span :class="{ 'grey--text': !item.maintenance }">{{ money(item.maintenance) }}</span>
                        </template>
                        <template v-slot:[`item.partner`]="{ item }">
                            <span :class="{ 'grey--text': !item.partner }">{{ money(item.partner) }}</span>
                        </template>
                        <template v-slot:[`item.other`]="{ item }">
                            <span :class="{ 'grey--text': !item.other }">{{ money(item.other) }}</span>
                        </template>
                        <template v-slot:[`item.revenue`]="{ item }">
                            <span :class="{ 'grey--text': !item.revenue }">{{ money(item.revenue) }}</span>
                        </template>
                        <template v-slot:[`item.cost`]="{ item }"><strong>{{ money(item.cost) }}</strong></template>
                        <template v-slot:[`item.margin`]="{ item }">
                            <span :class="item.margin < 0 ? 'red--text' : 'green--text text--darken-2'">{{ money(item.margin) }}</span>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>

            <!-- LANÇAMENTOS -->
            <v-card>
                <v-tabs v-model="tab">
                    <v-tab>Abastecimentos ({{ data.fuel.length }})</v-tab>
                    <v-tab>Manutenções ({{ data.maintenances.length }})</v-tab>
                    <v-tab>Outras despesas ({{ (data.expenses || []).length }})</v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab">
                    <v-tab-item>
                        <v-data-table dense :headers="fuelHeaders" :items="data.fuel" :items-per-page="10" class="app-table hover-table"
                            no-data-text="Nenhum abastecimento no período" @click:row="item => $router.push(`/abastecimentos/${item.id}`)">
                            <template v-slot:[`item.usage_date`]="{ item }">{{ dateTime(item.usage_date) }}</template>
                            <template v-slot:[`item.odometer`]="{ item }">{{ number(item.odometer) }} km</template>
                            <template v-slot:[`item.liters`]="{ item }">{{ number(item.liters) }} L</template>
                            <template v-slot:[`item.total`]="{ item }">{{ money(item.total) }}</template>
                        </v-data-table>
                    </v-tab-item>
                    <v-tab-item>
                        <v-data-table dense :headers="maintenanceHeaders" :items="data.maintenances" :items-per-page="10" class="app-table"
                            no-data-text="Nenhuma manutenção no período">
                            <template v-slot:[`item.date`]="{ item }">{{ date(item.date) }}</template>
                            <template v-slot:[`item.description`]="{ item }">
                                <span class="text-truncate d-inline-block" style="max-width: 420px">{{ item.work_performed || item.reported_issue || '-' }}</span>
                            </template>
                            <template v-slot:[`item.maintenance_status`]="{ item }">
                                <v-chip x-small outlined>{{ maintenanceStatus(item.maintenance_status) }}</v-chip>
                            </template>
                            <template v-slot:[`item.total_cost`]="{ item }">{{ money(item.total_cost) }}</template>
                        </v-data-table>
                    </v-tab-item>
                    <v-tab-item>
                        <v-data-table dense :headers="expenseHeaders" :items="data.expenses || []" :items-per-page="10" class="app-table hover-table"
                            no-data-text="Nenhuma despesa lançada para o veículo no período" @click:row="() => $router.push('/financeiro/despesas')">
                            <template v-slot:[`item.due_date`]="{ item }">{{ date(item.due_date) }}</template>
                            <template v-slot:[`item.amount`]="{ item }">{{ money(item.amount) }}</template>
                        </v-data-table>
                    </v-tab-item>
                </v-tabs-items>
            </v-card>
        </template>

        <!-- CUSTO FIXO -->
        <v-dialog v-model="cost.open" max-width="560px">
            <v-card>
                <v-card-title>{{ cost.id ? 'Editar custo fixo' : 'Novo custo fixo' }}</v-card-title>
                <v-card-text>
                    <v-form ref="costForm">
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-select dense outlined label="Tipo" :items="typeOptions" v-model="cost.type"
                                    :rules="[v => !!v || 'Informe o tipo']" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select dense outlined label="Recorrência" :items="frequencyOptions" v-model="cost.frequency" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-currency-field dense outlined prefix="R$" :label="amountLabel" v-model="cost.amount"
                                    :rules="[v => Number(v) > 0 || 'Informe o valor']" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field dense outlined type="date" :label="cost.frequency === 'once' ? 'Data' : 'Início'"
                                    v-model="cost.start_date" :rules="[v => !!v || 'Informe a data']" />
                            </v-col>
                            <template v-if="cost.frequency !== 'once'">
                                <v-col cols="12" sm="6">
                                    <v-text-field dense outlined type="number" min="1" label="Nº de parcelas (opcional)"
                                        v-model="cost.installments" hint="Calcula o término sozinho" persistent-hint />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field dense outlined type="date" label="Término (vazio = sem fim)" v-model="cost.end_date"
                                        :disabled="!!cost.installments" />
                                </v-col>
                            </template>
                            <v-col cols="12" sm="6">
                                <v-text-field dense outlined label="Descrição" v-model="cost.description" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field dense outlined label="Fornecedor / financeira" v-model="cost.supplier" />
                            </v-col>
                        </v-row>
                    </v-form>
                    <p class="caption grey--text mb-0">{{ frequencyHelp }}</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="cost.open = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" :loading="saving" @click="saveCost">Salvar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

const FREQUENCIES = [
    { value: 'monthly', text: 'Mensal' },
    { value: 'yearly', text: 'Anual (rateado em 12 meses)' },
    { value: 'once', text: 'Único' }
]

const MAINTENANCE_STATUS = {
    open: 'Aberta', in_progress: 'Em andamento', awaiting_parts: 'Aguardando peças',
    awaiting_approval: 'Aguardando aprovação', completed: 'Concluída'
}

const emptyCost = () => ({
    open: false, id: null, type: null, frequency: 'monthly', amount: null,
    start_date: moment().startOf('month').format('YYYY-MM-DD'), end_date: null, installments: null, description: '', supplier: ''
})

export default {
    name: 'VehicleCostDetails',

    data() {
        return {
            loading: false,
            saving: false,
            data: null,
            tab: 0,
            filter: {
                start: this.$route.query.start || moment().subtract(5, 'months').startOf('month').format('YYYY-MM-DD'),
                end: this.$route.query.end || moment().endOf('month').format('YYYY-MM-DD')
            },
            cost: emptyCost(),
            frequencyOptions: FREQUENCIES,
            costHeaders: [
                { text: 'Tipo', value: 'type', sortable: false },
                { text: 'Valor', value: 'amount', sortable: false },
                { text: 'Vigência', value: 'start_date', sortable: false },
                { text: 'Situação', value: 'active', sortable: false },
                { text: '', value: 'actions', sortable: false, width: 70 }
            ],
            monthHeaders: [
                { text: 'Mês', value: 'month', sortable: false },
                { text: 'Fixos', value: 'fixed', align: 'end', sortable: false },
                { text: 'Combustível', value: 'fuel', align: 'end', sortable: false },
                { text: 'Manutenção', value: 'maintenance', align: 'end', sortable: false },
                { text: 'Parceiro', value: 'partner', align: 'end', sortable: false },
                { text: 'Outras', value: 'other', align: 'end', sortable: false },
                { text: 'Custo', value: 'cost', align: 'end', sortable: false },
                { text: 'Receita', value: 'revenue', align: 'end', sortable: false },
                { text: 'Margem', value: 'margin', align: 'end', sortable: false },
                { text: 'Viagens', value: 'trips', align: 'end', sortable: false }
            ],
            fuelHeaders: [
                { text: 'Data', value: 'usage_date' },
                { text: 'Combustível', value: 'fuel_type' },
                { text: 'Hodômetro', value: 'odometer', align: 'end' },
                { text: 'Litros', value: 'liters', align: 'end' },
                { text: 'Valor', value: 'total', align: 'end' }
            ],
            expenseHeaders: [
                { text: 'Vencimento', value: 'due_date' },
                { text: 'Descrição', value: 'description' },
                { text: 'Categoria', value: 'category_name' },
                { text: 'Valor', value: 'amount', align: 'end' }
            ],
            maintenanceHeaders: [
                { text: 'Data', value: 'date' },
                { text: 'Serviço', value: 'description', sortable: false },
                { text: 'Situação', value: 'maintenance_status' },
                { text: 'Valor', value: 'total_cost', align: 'end' }
            ]
        }
    },

    computed: {
        summary() {
            return this.data ? this.data.summary : {}
        },
        vehicleTitle() {
            const vehicle = this.data && this.data.vehicle
            if (!vehicle) return 'Veículo'
            return `Frota ${vehicle.code} · ${vehicle.plate || ''}`.trim()
        },
        canManage() {
            return this.$check(['financial.vehicles.manage'])
        },
        typeOptions() {
            const types = (this.data && this.data.types) || {}
            return Object.keys(types).map(value => ({ value, text: types[value] }))
        },
        amountLabel() {
            return { monthly: 'Valor mensal', yearly: 'Valor anual', once: 'Valor' }[this.cost.frequency] || 'Valor'
        },
        frequencyHelp() {
            if (this.cost.frequency === 'yearly') return 'O valor anual é dividido em 12 meses para comparar com a receita de cada mês (ex.: IPVA, seguro).'
            if (this.cost.frequency === 'once') return 'Entra inteiro no mês da data informada.'
            return 'Entra todo mês na vigência, proporcional aos dias quando o período começa ou termina no meio do mês.'
        }
    },

    created() {
        this.load()
    },

    methods: {
        async load() {
            this.loading = true
            try {
                const response = await axios.get(`/api/v2/financial/vehicle-costs/${this.$route.params.id}`, { params: this.filter })
                this.data = response.data.content
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.loading = false
            }
        },

        setRange(range) {
            const end = moment().endOf('month')
            const start = range === 'month' ? moment().startOf('month')
                : moment().subtract(range === '6m' ? 5 : 11, 'months').startOf('month')
            this.filter.start = start.format('YYYY-MM-DD')
            this.filter.end = end.format('YYYY-MM-DD')
            this.load()
        },

        openCost(item) {
            this.cost = item
                ? { ...emptyCost(), ...item, amount: Number(item.amount), installments: item.installments || null, open: true }
                : { ...emptyCost(), open: true }
            this.$nextTick(() => this.$refs.costForm && this.$refs.costForm.resetValidation())
        },

        async saveCost() {
            if (!this.$refs.costForm.validate()) return

            this.saving = true
            const payload = {
                type: this.cost.type,
                frequency: this.cost.frequency,
                amount: this.cost.amount,
                start_date: this.cost.start_date,
                end_date: this.cost.installments ? null : this.cost.end_date,
                installments: this.cost.installments || null,
                description: this.cost.description,
                supplier: this.cost.supplier
            }

            try {
                const response = this.cost.id
                    ? await axios.put(`/api/v2/financial/vehicle-fixed-costs/${this.cost.id}`, payload)
                    : await axios.post(`/api/v2/financial/vehicle-costs/${this.$route.params.id}/fixed-costs`, payload)
                this.data.fixed_costs = response.data.content
                this.cost.open = false
                this.$dialog.notify.success('Custo fixo salvo', { position: 'top-right', timeout: 3000 })
                this.load()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.saving = false
            }
        },

        async removeCost(item) {
            const confirmed = await this.$dialog.confirm({
                title: 'Remover custo fixo',
                text: `Remover ${item.type_name.toLowerCase()} de ${this.money(item.amount)}? Ele deixa de entrar em todos os períodos.`,
                actions: { false: 'Voltar', true: 'Remover' }
            })
            if (!confirmed) return

            try {
                await axios.delete(`/api/v2/financial/vehicle-fixed-costs/${item.id}`)
                this.load()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            }
        },

        frequencyText(item) {
            return { monthly: '/mês', yearly: '/ano', once: 'único' }[item.frequency] || ''
        },

        maintenanceStatus(status) {
            return MAINTENANCE_STATUS[status] || status
        },

        monthName(month) {
            const label = moment(`${month}-01`).locale('pt-br').format('MMM/YYYY')
            return label.charAt(0).toUpperCase() + label.slice(1)
        },

        money(value) {
            return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },

        percent(value) {
            return `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
        },

        number(value) {
            return Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
        },

        date(value) {
            return value ? moment.utc(value).format('DD/MM/YYYY') : '-'
        },

        dateTime(value) {
            return value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'
        }
    }
}
</script>
